/**
 * 支付助手「防骚扰」调度
 *
 * - 3s：券到期提醒（最高优先）
 * - 30s：浏览沉默问询（本轮只弹一次；选购交互后取消）
 * - 「不再提示」：24h 静音（tips 模块写 localStorage）
 *
 * 【兜底】定时器回调 / tips 动态加载须吞错并重置 tipShowing，
 * 失败不得产生 UnhandledRejection 或卡住后续气泡
 */
import {
  isSilenceCancelled,
  markSilenceCancelled,
  notifyShoppingInteract,
  resetSilenceScheduling,
  scheduleSilenceTimer,
} from './shoppingInteract';

const COUPON_TIP_DELAY_MS = 3 * 1000;
const SILENCE_TIP_DELAY_MS = 30 * 1000;
const COUPON_EXPIRE_WINDOW_MS = 72 * 60 * 60 * 1000;

const TIP_ID_COUPON = 'anti_harass_coupon_expire';
const TIP_ID_SILENCE = 'anti_harass_browse_silence';

let couponTimer = null;
let started = false;
let couponChecked = false;
let tipShowing = false;
/** @type {{ getExpiringCoupon?: Function } | null} */
let hostCtx = null;

function clearTimer(timer) {
  if (timer) clearTimeout(timer);
  return null;
}

function resetState() {
  couponTimer = clearTimer(couponTimer);
  resetSilenceScheduling();
  started = false;
  couponChecked = false;
  tipShowing = false;
  hostCtx = null;
}

function loadTipsApi() {
  return import('./tips');
}

/**
 * 从可用券列表找出 72h 内最早到期的一张
 */
export function findExpiringCoupon(couponList = []) {
  const now = Date.now();
  const windowEnd = now + COUPON_EXPIRE_WINDOW_MS;
  const list = Array.isArray(couponList) ? couponList : [];

  const candidates = list.filter((coupon) => {
    if (!coupon || !coupon.EXPIRY_TIME) return false;
    const expireAt = +coupon.EXPIRY_TIME * 1000;
    return expireAt > now && expireAt <= windowEnd;
  });

  candidates.sort((a, b) => +a.EXPIRY_TIME - +b.EXPIRY_TIME);
  return candidates[0] || null;
}

function handleMute() {
  tipShowing = false;
  notifyShoppingInteract();
}

async function tryShowCouponTip() {
  if (!hostCtx || tipShowing) return false;
  const { isAgentTipMuted, showCouponExpireTip, hideAgentTip } =
    await loadTipsApi();
  if (isAgentTipMuted()) return false;

  const coupon = hostCtx.getExpiringCoupon
    ? hostCtx.getExpiringCoupon()
    : null;
  if (!coupon) return false;

  tipShowing = true;
  await showCouponExpireTip({
    tipId: TIP_ID_COUPON,
    coupon,
    onMute: handleMute,
    onViewDetail: async () => {
      tipShowing = false;
      notifyShoppingInteract();
      await hideAgentTip(TIP_ID_COUPON);
    },
    onClose: () => {
      tipShowing = false;
    },
    onAutoHide: () => {
      tipShowing = false;
    },
  });
  return true;
}

async function tryShowSilenceTip() {
  if (!hostCtx || isSilenceCancelled()) return false;
  const { isAgentTipMuted, showBrowseSilenceTip, hideAgentTip } =
    await loadTipsApi();
  if (isAgentTipMuted()) return false;

  if (tipShowing) {
    scheduleSilenceTimer(() => {
      tryShowSilenceTip();
    }, 1000);
    return false;
  }

  tipShowing = true;
  markSilenceCancelled();
  await showBrowseSilenceTip({
    tipId: TIP_ID_SILENCE,
    onMute: handleMute,
    onViewDetail: async () => {
      tipShowing = false;
      notifyShoppingInteract();
      await hideAgentTip(TIP_ID_SILENCE);
    },
    onClose: () => {
      tipShowing = false;
    },
  });
  return true;
}

/**
 * @param {Object} ctx
 * @param {Function} ctx.getExpiringCoupon 返回即将过期券或 null
 *
 * 【兜底】定时器与动态 import 失败须 .catch，并重置 tipShowing，
 * 避免 UnhandledRejection 或 tip 状态机卡死影响后续气泡/下单
 */
export function startAntiHarassment(ctx = {}) {
  if (started) return;
  started = true;
  hostCtx = ctx;

  void loadTipsApi()
    .then(({ isAgentTipMuted }) => {
      if (!started || isAgentTipMuted()) return;

      couponTimer = setTimeout(() => {
        couponChecked = true;
        // 【兜底】券气泡失败：重置 tipShowing，不抛到宿主
        void tryShowCouponTip().catch((err) => {
          console.warn('[PayAssistant] coupon tip failed', err);
          tipShowing = false;
        });
      }, COUPON_TIP_DELAY_MS);

      scheduleSilenceTimer(() => {
        if (isSilenceCancelled()) return;
        if (!couponChecked) {
          scheduleSilenceTimer(() => {
            // 【兜底】沉默气泡重试同样吞错
            void tryShowSilenceTip().catch((err) => {
              console.warn('[PayAssistant] silence tip retry failed', err);
              tipShowing = false;
            });
          }, 200);
          return;
        }
        // 【兜底】沉默气泡失败：重置 tipShowing
        void tryShowSilenceTip().catch((err) => {
          console.warn('[PayAssistant] silence tip failed', err);
          tipShowing = false;
        });
      }, SILENCE_TIP_DELAY_MS);
    })
    .catch((err) => {
      // 【兜底】tips 模块加载失败：允许后续再次 start
      console.warn('[PayAssistant] antiHarassment tips load failed', err);
      started = false;
    });
}

export function stopAntiHarassment() {
  loadTipsApi()
    .then(({ hideAgentTip }) => hideAgentTip().catch(() => { }))
    .catch(() => { });
  resetState();
}
